import { col, type Shape } from './base.ts';

export const OPT_SRM_SCENARIO_META: Shape = [
  col('scenario', 'unitless', 'Optimization scenario name/identifier.'),
  col('executionDate', 'unitless', 'When this optimization scenario was executed.'),
  col('isOfficial', 'unitless', 'Whether this is the official approved scenario (1 = approved, 0 = exploratory).'),
];

/**
 * SRM (Sector Reservoir Model) optimized rates on
 * `OptimizedRates_SRM_String_Agent`. The engine marker (`SRM`) is a TRAILING
 * suffix, not a leading prefix, so each column's `suffix` carries the full
 * verbatim source tail and `canonical` is the de-marked name; applied with the
 * default empty prefix. VERBATIM source typos preserved: `optimizeGasRateSRM`
 * and `optimizeWaterRateSRM` drop the `d` of `optimized` (a typo would silently
 * degrade to `unknownCol`).
 */
export const OPT_SRM_RATES: Shape = [
  col('oilRateSrm', 'liquidRate', 'SRM-optimized oil production rate.', { suffix: 'optimizedOilRateSRM' }),
  // Source typos preserved verbatim ("optimize" missing the trailing `d`).
  col('gasRateSrm', 'gasRate', 'SRM-optimized gas production rate.', { suffix: 'optimizeGasRateSRM' }),
  col('waterRateSrm', 'liquidRate', 'SRM-optimized water production rate.', { suffix: 'optimizeWaterRateSRM' }),
  col('waterInjectionRateSrm', 'liquidRate', 'SRM-optimized water injection rate.', {
    suffix: 'optimizedWaterInjRateSRM',
  }),
  col('gasInjectionRateSrm', 'gasRate', 'SRM-optimized gas injection rate.', { suffix: 'optimizedGasInjRateSRM' }),
];

/**
 * GAP (network-model) optimized production/injection rates on
 * `OptimizedRates_SRM_String_Agent`. Trailing `GAP` engine marker absorbed into
 * each verbatim `suffix`; applied with the default empty prefix.
 */
export const OPT_GAP_RATES: Shape = [
  col('oilRateGap', 'liquidRate', 'GAP network-optimized oil production rate.', { suffix: 'optimizedOilRateGAP' }),
  col('gasRateGap', 'gasRateModel', 'GAP network-optimized gas production rate.', { suffix: 'optimizedGasRateGAP' }),
  col('waterRateGap', 'liquidRate', 'GAP network-optimized water production rate.', {
    suffix: 'optimizedWaterRateGAP',
  }),
  col('waterInjectionRateGap', 'liquidRate', 'GAP network-optimized water injection rate.', {
    suffix: 'optimizedWaterInjRateGAP',
  }),
  col('gasInjectionRateGap', 'gasRateModel', 'GAP network-optimized gas injection rate.', {
    suffix: 'optimizedGasInjRateGAP',
  }),
];

/**
 * GAP-optimized well/completion setpoints + reservoir-condition rates on
 * `OptimizedRates_SRM_String_Agent`. Verbatim trailing markers in each `suffix`;
 * `prefix:''`. VERBATIM source casing preserved: `optimizedBHFPGAP` reads BHFP
 * (not BHP); `optimizedWHP` carries no engine marker.
 */
export const OPT_GAP_SETPOINTS: Shape = [
  col('chokeSizeGap', 'chokeSize', 'GAP-optimized choke size setting.', { suffix: 'optimizedChokeSizeGAP' }),
  col('gasLiftRateGap', 'gasRateModel', 'GAP-optimized gas-lift injection rate.', {
    suffix: 'optimizedGasLiftRateGAP',
  }),
  col('espFrequencyGap', 'frequency', 'GAP-optimized ESP pump frequency.', { suffix: 'optimizedESPFrequencyGAP' }),
  // Source spelling `BHFP` preserved verbatim (others use BHP/WHP).
  col('bhfpGap', 'pressureDelta', 'GAP-optimized bottom-hole flowing pressure.', { suffix: 'optimizedBHFPGAP' }),
  col('whp', 'pressureAbsolute', 'Optimized well-head pressure.', { suffix: 'optimizedWHP' }),
  col('prodRateResCond', 'liquidRate', 'Optimized production rate at reservoir conditions (for VRR).', {
    suffix: 'optimizedProdRateResCond',
  }),
  col('injectionRateResCond', 'liquidRate', 'Optimized injection rate at reservoir conditions (for VRR).', {
    suffix: 'optimizedInjRateResCond',
  }),
];

/**
 * Prosper/choke/PVT model-validation alarms on `ModelMatchedRates_String_Agent`.
 * Bare source columns — `suffix === canonical`, `prefix:''`.
 */
export const MODEL_MATCH_ALARMS: Shape = [
  col('modelAlarms', 'unitless', 'Prosper model validation alarm.'),
  col('historicalAlarms', 'unitless', 'Historical-data validation alarm.'),
  col('chokeAlarms', 'unitless', 'Choke model validation alarm.'),
  col('pvtAlarms', 'unitless', 'PVT validation alarm.'),
];

/**
 * Model-matched rates on `ModelMatchedRates_String_Agent`, applied with
 * `prefix:'modelMatched'`. VERBATIM source casing preserved: the phase tail is
 * LOWERCASE-leading (`modelMatchedoilRate`, not `…OilRate`), so the suffixes are
 * authored lowercase — `PHASE_RATES`/`PHASE_RATES_STD` (PascalCase) would NOT
 * resolve. `modelMatchedBHP` keeps the uppercase `BHP` tail. Canonicals keep the
 * `modelMatched` match-source marker so they stay distinct from the choke-matched
 * and SC/LC ratio families composed alongside them in the same view.
 */
export const MODEL_MATCHED_RATES: Shape = [
  col('modelMatchedOilRate', 'liquidRate', 'Model-matched oil production rate.', { suffix: 'oilRate' }),
  col('modelMatchedGasRate', 'gasRate', 'Model-matched gas production rate.', { suffix: 'gasRate' }),
  col('modelMatchedWaterRate', 'liquidRate', 'Model-matched water production rate.', { suffix: 'waterRate' }),
  col('modelMatchedLiquidRate', 'liquidRate', 'Model-matched liquid production rate.', { suffix: 'liquidRate' }),
  col('modelMatchedBhp', 'pressureAbsolute', 'Model-matched bottom-hole pressure.', { suffix: 'BHP' }),
];

/**
 * Choke-matched rates on `ModelMatchedRates_String_Agent`, applied with
 * `prefix:'chokeMatched'`. Lowercase-leading phase tail preserved verbatim
 * (`chokeMatchedliquidRate`, `chokeMatchedgasRate`); only liquid + gas exist.
 * Canonicals keep the `chokeMatched` match-source marker so they stay distinct
 * from the model-matched rates composed alongside them in the same view.
 */
export const CHOKE_MATCHED_RATES: Shape = [
  col('chokeMatchedLiquidRate', 'liquidRate', 'Choke-matched liquid production rate.', { suffix: 'liquidRate' }),
  col('chokeMatchedGasRate', 'gasRate', 'Choke-matched gas production rate.', { suffix: 'gasRate' }),
];

/**
 * Standard-conditions (SC) ratios on `ModelMatchedRates_String_Agent`. The `SC`
 * marker is a TRAILING suffix, so each verbatim `suffix` carries it; the
 * `canonical` keeps the `Sc` condition marker so the SC measures stay distinct
 * from the model/choke-matched rates and the line-conditions (LC) ratios
 * composed alongside them in the same view. Applied with the default empty prefix.
 */
export const MODEL_RATIOS_SC: Shape = [
  col('oilRateSc', 'liquidRate', 'Oil rate at standard conditions.', { suffix: 'oilRateSC' }),
  col('gasRateSc', 'gasRate', 'Gas rate at standard conditions.', { suffix: 'gasRateSC' }),
  col('totalGorSc', 'gasOilRatio', 'Total gas-oil ratio at standard conditions.', { suffix: 'totalGorSC' }),
  col('waterCutSc', 'fraction', 'Water cut at standard conditions.', { suffix: 'waterCutSC' }),
  col('cgrSc', 'liquidGasRatio', 'Condensate-gas ratio at standard conditions.', { suffix: 'cgrSC' }),
  col('wgrSc', 'liquidGasRatio', 'Water-gas ratio at standard conditions.', { suffix: 'wgrSC' }),
];

/**
 * Line-conditions (LC) ratios on `ModelMatchedRates_String_Agent`. Trailing `LC`
 * marker absorbed into each verbatim `suffix`; `prefix:''`. The `canonical` keeps
 * the `Lc` condition marker so the LC measures stay distinct from the
 * standard-conditions (SC) ratios composed alongside them in the same view.
 */
export const MODEL_RATIOS_LC: Shape = [
  col('gorLc', 'gasOilRatio', 'Gas-oil ratio at line conditions.', { suffix: 'gorLC' }),
  col('waterCutLc', 'fraction', 'Water cut at line conditions.', { suffix: 'waterCutLC' }),
  col('cgrLc', 'liquidGasRatio', 'Condensate-gas ratio at line conditions.', { suffix: 'cgrLC' }),
  col('wgrLc', 'liquidGasRatio', 'Water-gas ratio at line conditions.', { suffix: 'wgrLC' }),
];

/**
 * Well-test standard-conditions phase rates on
 * `MeasuredRates_WellTest_String_Agent`, applied with `prefix:'wellTest'`. The
 * three std rates match the first three `PHASE_RATES_STD` members
 * (`OilRateStd`/`GasRateStd`/`WaterRateStd`), but the source's liquid column is
 * `wellTestLiquidRatePts` (a `LiquidRatePts` tail, NOT `LiquidRateStd`) — so the
 * liquid member is authored here rather than reusing the full `PHASE_RATES_STD`.
 */
